import { useCallback, useEffect, useRef } from "react";
/** Default minimum time (ms) the user must spend before qualifying as "engaged". */ const DEFAULT_THRESHOLD_MS = 10_000;
/**
 * Tracks user engagement while `isActive` is true (time spent + click/scroll interaction).
 *
 * Returns:
 * - `containerRef` — attach to the container element whose interactions count.
 * - `hasEngaged`   — call at decision time; returns `true` when elapsed ≥ threshold OR any interaction occurred.
 *
 * Resets automatically every time `isActive` flips to `true`.
 */ export function useEngagementGate(isActive, thresholdMs = DEFAULT_THRESHOLD_MS) {
    const startRef = useRef(null);
    const hasInteractedRef = useRef(false);
    const containerRef = useRef(null);
    // Reset tracking each time the active state changes.
    useEffect(()=>{
        if (isActive) {
            startRef.current = Date.now();
            hasInteractedRef.current = false;
        } else {
            startRef.current = null;
        }
    }, [
        isActive
    ]);
    // Attach click/scroll listeners while active. A single interaction qualifies.
    useEffect(()=>{
        const container = containerRef.current;
        if (!isActive || !container) {
            return;
        }
        const onInteraction = ()=>{
            hasInteractedRef.current = true;
        };
        container.addEventListener("click", onInteraction, {
            once: true,
            capture: true
        });
        container.addEventListener("scroll", onInteraction, {
            once: true,
            capture: true
        });
        return ()=>{
            container.removeEventListener("click", onInteraction, {
                capture: true
            });
            container.removeEventListener("scroll", onInteraction, {
                capture: true
            });
        };
    }, [
        isActive
    ]);
    const hasEngaged = useCallback(()=>{
        if (startRef.current === null) {
            return false;
        }
        const elapsed = Date.now() - startRef.current;
        return elapsed >= thresholdMs || hasInteractedRef.current;
    }, [
        thresholdMs
    ]);
    return {
        containerRef,
        hasEngaged
    };
}
